import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Trophy, Shuffle, Lock, RotateCcw, Pencil, ListOrdered } from "lucide-react";
import { toast } from "sonner";
import { safeFetch, fetchWithRetry } from "../utils/api";
import ConfirmDialog from "./ConfirmDialog";
import Tooltip from "./Tooltip";

const GROUP_NAMES = ["Group A", "Group B", "Group C", "Group D"];

const ROUND_LABELS_BY_SIZE = {
    2: ["Final"],
    4: ["Semifinals", "Final"],
    8: ["Quarterfinals", "Semifinals", "Final"],
    16: ["Round of 16", "Quarterfinals", "Semifinals", "Final"],
};

const bracketSizeFor = (groupCount, advancePerGroup) => {
    const qualifiers = groupCount * advancePerGroup;
    let size = 2;
    while (size < qualifiers) size *= 2;
    return size;
};

const inputClass =
    "text-sm border border-border rounded-lg px-3 py-2 bg-bg text-ds-text focus:outline-none focus:ring-2 focus:ring-mustard disabled:bg-surface disabled:text-text-muted disabled:cursor-not-allowed";

const labelClass = "block text-xs font-sans font-bold text-text-muted uppercase tracking-wide mb-1";

const LeagueCupSettings = ({ leaguePk, onChanged }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState(false);
    // Which destructive action is awaiting confirmation: null | "reset" | "delete"
    const [confirmAction, setConfirmAction] = useState(null);
    const [isConfigured, setIsConfigured] = useState(false);
    const [editable, setEditable] = useState(true);
    const [league, setLeague] = useState(null);
    const [cupStatus, setCupStatus] = useState(null);

    // Form state
    const [name, setName] = useState("League Cup");
    const [startWeek, setStartWeek] = useState(1);
    const [groupCount, setGroupCount] = useState(2);
    const [groupStageWeeks, setGroupStageWeeks] = useState(null); // null = auto
    const [advancePerGroup, setAdvancePerGroup] = useState(1);
    const [roundLength, setRoundLength] = useState(2);

    // Manual assignment editor: { [provider_team_id]: groupName }
    const [teamGroups, setTeamGroups] = useState({});
    // Whether the group editor is open (vs. the read-only group display).
    const [editingGroups, setEditingGroups] = useState(false);
    // Local override that lets the owner edit structural settings on a
    // started cup. The server enables a soft-reset path on PATCH when this
    // produces structural changes (results cleared, groups preserved).
    const [forceEditing, setForceEditing] = useState(false);

    // Snapshots of the last server-saved state, used to detect unsaved changes.
    const [savedConfig, setSavedConfig] = useState(null);
    const [savedAssignments, setSavedAssignments] = useState({});

    const applyPayload = useCallback((data) => {
        setIsConfigured(data.is_configured);
        const isEditable = data.editable !== false;
        setEditable(isEditable);
        setLeague(data.league || null);
        if (data.cup) {
            const cfg = data.cup.config;
            setName(cfg.name);
            setStartWeek(cfg.start_week);
            setGroupCount(cfg.group_count);
            setGroupStageWeeks(cfg.group_stage_weeks);
            setAdvancePerGroup(cfg.advance_per_group);
            setRoundLength(cfg.knockout_round_length_weeks);
            setCupStatus(data.cup.status);
            setSavedConfig({
                name: cfg.name,
                start_week: cfg.start_week,
                group_count: cfg.group_count,
                group_stage_weeks: cfg.group_stage_weeks,
                advance_per_group: cfg.advance_per_group,
                knockout_round_length_weeks: cfg.knockout_round_length_weeks,
            });
        } else {
            setCupStatus(null);
            setSavedConfig(null);
            const minWeek = data.league?.min_week || 1;
            // For completed seasons, default the cup to the league's first
            // playable week so a retroactive cup spans the whole season.
            // For in-progress seasons, default to the current week (clamped to
            // min_week) so the cup starts going forward.
            if (data.league?.is_season_complete) {
                setStartWeek(minWeek);
            } else if (data.league?.current_week) {
                setStartWeek(Math.max(minWeek, data.league.current_week));
            }
        }
        const mapping = {};
        Object.entries(data.assignments || {}).forEach(([groupName, members]) => {
            members.forEach((m) => { mapping[m.provider_team_id] = groupName; });
        });
        setTeamGroups(mapping);
        setSavedAssignments(mapping);
        // Open the editor automatically when teams still need a group (e.g. a
        // manual-assignment cup or a freshly reset cup). Only ever force it
        // open, never closed — so actions like Randomize keep the editor up.
        const teams = data.league?.teams || [];
        const incomplete = teams.some((t) => !mapping[t.provider_team_id]);
        if (data.is_configured && isEditable && teams.length > 0 && incomplete) {
            setEditingGroups(true);
        }
    }, []);

    useEffect(() => {
        if (!leaguePk) return;
        safeFetch(`/api/league/${leaguePk}/cup/`, {}, false, 1)
            .then((data) => applyPayload(data))
            .catch(() => setLoadError(true))
            .finally(() => setLoading(false));
    }, [leaguePk, applyPayload]);

    // Prior-season seeding ("Seed by Last Season") is only offered when the
    // backend can link this league to last year's season and rank the
    // current teams by last year's regular-season Points For.
    const [priorSeeding, setPriorSeeding] = useState(null);
    useEffect(() => {
        if (!leaguePk) return;
        safeFetch(`/api/league/${leaguePk}/cup/prior-seeding/`, {}, false, 0)
            .then((data) => setPriorSeeding(data?.available ? data : null))
            .catch(() => setPriorSeeding(null));
    }, [leaguePk]);

    const teamCount = league?.teams?.length || 0;
    // Default group stage length is one week shy of a full round-robin among
    // the largest group: max(group_size) - 1. The user can still override this
    // via the input; the override resets whenever group_count changes (below).
    const maxGroupSize = groupCount ? Math.ceil(teamCount / groupCount) : 0;
    const defaultStageWeeks = Math.max(1, maxGroupSize - 1);
    const effectiveStageWeeks = groupStageWeeks ?? defaultStageWeeks;

    // Max teams that can advance from each group. With uneven group sizes, the
    // smallest group caps the bracket — but for simplicity we use the floor of
    // teams/groups, matching the typical "(N) groups of (k)" layout.
    const teamsPerGroup = groupCount ? Math.floor(teamCount / groupCount) : 0;
    const advanceOptions = useMemo(() => {
        if (teamsPerGroup < 1) return [1];
        return Array.from({ length: teamsPerGroup }, (_, i) => i + 1);
    }, [teamsPerGroup]);

    // Clamp advancePerGroup whenever the dropdown options shrink (e.g. user
    // raised group count enough that fewer teams fit per group).
    useEffect(() => {
        if (teamsPerGroup > 0 && advancePerGroup > teamsPerGroup) {
            setAdvancePerGroup(teamsPerGroup);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teamsPerGroup]);

    // Pre-populate the editor with a client-side random shuffle for new cups
    // so the group table is visible (and meaningful) the moment teams load.
    // Once a cup is configured, we leave assignments alone — the server is the
    // source of truth.
    useEffect(() => {
        if (isConfigured) return;
        const teams = league?.teams || [];
        if (teams.length === 0 || groupCount === 0) return;
        const validGroups = new Set(GROUP_NAMES.slice(0, groupCount));
        const allAssigned = teams.every((t) =>
            validGroups.has(teamGroups[t.provider_team_id])
        );
        if (allAssigned) return;
        const ids = teams.map((t) => t.provider_team_id);
        for (let i = ids.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [ids[i], ids[j]] = [ids[j], ids[i]];
        }
        const next = {};
        const groups = GROUP_NAMES.slice(0, groupCount);
        ids.forEach((id, index) => { next[id] = groups[index % groups.length]; });
        setTeamGroups(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isConfigured, league, groupCount]);

    const schedule = useMemo(() => {
        const size = bracketSizeFor(groupCount, advancePerGroup);
        const labels = ROUND_LABELS_BY_SIZE[size] || [];
        const knockoutWeeks = labels.length * roundLength;
        const endWeek = Number(startWeek) + effectiveStageWeeks + knockoutWeeks - 1;
        return {
            size,
            wildcards: size - groupCount * advancePerGroup,
            labels,
            endWeek,
            overruns: league ? endWeek > league.reg_season_count : false,
        };
    }, [groupCount, advancePerGroup, roundLength, startWeek, effectiveStageWeeks, league]);

    const groupNames = GROUP_NAMES.slice(0, groupCount);

    // Teams grouped for the read-only display.
    const teamsByGroup = {};
    groupNames.forEach((g) => { teamsByGroup[g] = []; });
    (league?.teams || []).forEach((team) => {
        const g = teamGroups[team.provider_team_id];
        if (teamsByGroup[g]) teamsByGroup[g].push(team);
    });

    // Has the config form diverged from what's saved on the server?
    const hasConfigChanges = useMemo(() => {
        if (!savedConfig) return true;
        const current = {
            name,
            start_week: Number(startWeek),
            group_count: Number(groupCount),
            group_stage_weeks: Number(effectiveStageWeeks),
            advance_per_group: Number(advancePerGroup),
            knockout_round_length_weeks: Number(roundLength),
        };
        return Object.keys(current).some((k) => current[k] !== savedConfig[k]);
    }, [
        savedConfig,
        name,
        startWeek,
        groupCount,
        effectiveStageWeeks,
        advancePerGroup,
        roundLength,
    ]);

    // Have group assignments changed since the last save?
    const hasGroupChanges = useMemo(() => {
        const ids = (league?.teams || []).map((t) => t.provider_team_id);
        return ids.some(
            (id) => (teamGroups[id] || "") !== (savedAssignments[id] || "")
        );
    }, [league, teamGroups, savedAssignments]);

    const refreshFromResponse = async (resp) => {
        const data = await resp.json();
        if (resp.ok) {
            applyPayload(data);
            onChanged?.();
            return { ok: true, data };
        }
        return { ok: false, data };
    };

    // Lightweight post-save update: bring local "saved snapshot" state in
    // sync with the server response so hasConfigChanges/hasGroupChanges
    // reset to false, and refresh the lock/status badges — without
    // re-applying the entire payload (which would clobber the user's form
    // input mid-edit). The Overview tab is still notified via onChanged().
    const markSavedFromResponse = async (resp) => {
        const data = await resp.json();
        if (!resp.ok) return { ok: false, data };
        setIsConfigured(data.is_configured);
        setEditable(data.editable !== false);
        if (data.cup) setCupStatus(data.cup.status);
        if (data.cup?.config) {
            const cfg = data.cup.config;
            setSavedConfig({
                name: cfg.name,
                start_week: cfg.start_week,
                group_count: cfg.group_count,
                group_stage_weeks: cfg.group_stage_weeks,
                advance_per_group: cfg.advance_per_group,
                knockout_round_length_weeks: cfg.knockout_round_length_weeks,
            });
        }
        const mapping = {};
        Object.entries(data.assignments || {}).forEach(([groupName, members]) => {
            members.forEach((m) => { mapping[m.provider_team_id] = groupName; });
        });
        setSavedAssignments(mapping);
        // Keep the user's local teamGroups state untouched unless the server
        // has assignments we need to surface (e.g. first save after Create).
        if (Object.keys(teamGroups).length === 0 && Object.keys(mapping).length > 0) {
            setTeamGroups(mapping);
        }
        onChanged?.();
        return { ok: true, data };
    };

    const configBody = () => ({
        name,
        start_week: Number(startWeek),
        group_count: Number(groupCount),
        group_stage_weeks: Number(effectiveStageWeeks),
        advance_per_group: Number(advancePerGroup),
        knockout_round_length_weeks: Number(roundLength),
    });

    // Build a {GroupName: [teamId, ...]} payload from the current teamGroups
    // map, restricted to the active group names. Returns null when any team is
    // unassigned (caller should skip the explicit assignment payload).
    const assignmentsPayload = () => {
        const teams = league?.teams || [];
        if (teams.length === 0) return null;
        const groups = {};
        groupNames.forEach((g) => { groups[g] = []; });
        for (const team of teams) {
            const g = teamGroups[team.provider_team_id];
            if (!groups[g]) return null; // unassigned or stale group
            groups[g].push(String(team.provider_team_id));
        }
        return groups;
    };

    // Client-side group randomizer used both pre- and post-create. Doing this
    // in-memory avoids an API round-trip that would otherwise overwrite any
    // unsaved config changes (group count, start week, etc).
    const shuffleAssignments = () => {
        const teams = league?.teams || [];
        if (teams.length === 0 || groupNames.length === 0) return;
        const ids = teams.map((t) => t.provider_team_id);
        for (let i = ids.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [ids[i], ids[j]] = [ids[j], ids[i]];
        }
        const next = {};
        ids.forEach((id, index) => {
            next[id] = groupNames[index % groupNames.length];
        });
        setTeamGroups(next);
    };

    const handleCreate = async () => {
        setSaving(true);
        try {
            const body = configBody();
            const assignments = assignmentsPayload();
            // If the user has fully assigned teams in the editor, send those
            // along so the backend skips its random fallback. Otherwise honor
            // the chosen mode (random or manual).
            if (assignments) body.assignments = assignments;
            const resp = await fetchWithRetry(
                `/api/league/${leaguePk}/cup/`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(body),
                },
                0
            );
            const { ok, data } = await refreshFromResponse(resp);
            if (ok) {
                toast.success("League Cup created!");
            } else {
                (data.errors || [data.error || "Failed to create the Cup."]).forEach((e) =>
                    toast.error(e)
                );
            }
        } catch {
            toast.error("An unexpected error occurred.");
        } finally {
            setSaving(false);
        }
    };

    const handleUpdate = async () => {
        setSaving(true);
        try {
            // Snapshot the editor's local groups before PATCH so we can
            // restore/persist them after the response (which may have wiped
            // server-side groups when group_count changed).
            const localGroups = { ...teamGroups };
            const localGroupCount = Number(groupCount);

            const resp = await fetchWithRetry(
                `/api/league/${leaguePk}/cup/`,
                {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(configBody()),
                },
                0
            );
            const { ok, data } = await markSavedFromResponse(resp);
            if (!ok) {
                (data.errors || [data.error || "Failed to save Cup settings."]).forEach((e) =>
                    toast.error(e)
                );
                return;
            }

            // PATCH applies new config; the server may have cleared groups
            // (when group_count changed, or as part of the soft-reset path
            // for a started cup). Push the user's local selections to make
            // the editor and DB agree without a separate "Save Groups" step
            // — but only when they form a complete, plausible assignment for
            // the *new* group count. Otherwise open the editor so the user
            // can finish reassigning.
            const newGroupCount = data?.cup?.config?.group_count || localGroupCount;
            const targetNames = GROUP_NAMES.slice(0, newGroupCount);
            const validNames = new Set(targetNames);
            const teams = data?.league?.teams || league?.teams || [];

            // Server-side assignments after the PATCH, keyed by team.
            const serverMap = {};
            Object.entries(data?.assignments || {}).forEach(([g, members]) => {
                members.forEach((m) => { serverMap[m.provider_team_id] = g; });
            });
            const groupsDiffer = teams.some(
                (t) =>
                    (localGroups[t.provider_team_id] || "") !==
                    (serverMap[t.provider_team_id] || "")
            );
            const allAssignedLocally =
                teams.length > 0 &&
                teams.every((t) => validNames.has(localGroups[t.provider_team_id]));
            const everyGroupUsed = targetNames.every((g) =>
                teams.some((t) => localGroups[t.provider_team_id] === g)
            );

            if (groupsDiffer) {
                if (allAssignedLocally && everyGroupUsed) {
                    const result = await persistGroups({
                        silent: true,
                        groups: localGroups,
                        groupNamesOverride: targetNames,
                    });
                    if (!result.ok) {
                        setEditingGroups(true);
                        toast.warning(
                            "Settings saved — but the group assignments need attention. Adjust the groups and press Save Groups."
                        );
                        return;
                    }
                } else {
                    setEditingGroups(true);
                    toast.success("League Cup settings saved!");
                    toast.info(
                        "Assign every team to a group, then press Save Groups."
                    );
                    return;
                }
            }
            setForceEditing(false);
            setEditingGroups(false);
            toast.success("League Cup settings saved!");
        } catch {
            toast.error("An unexpected error occurred.");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setSaving(true);
        try {
            const resp = await fetchWithRetry(
                `/api/league/${leaguePk}/cup/`,
                { method: "DELETE" },
                0
            );
            if (resp.ok) {
                setIsConfigured(false);
                setCupStatus(null);
                setTeamGroups({});
                onChanged?.();
                toast.success("League Cup deleted.");
            } else {
                const data = await resp.json();
                toast.error(data.error || "Failed to delete the Cup.");
            }
        } catch {
            toast.error("An unexpected error occurred.");
        } finally {
            setSaving(false);
            setConfirmAction(null);
        }
    };

    const handleReset = async () => {
        setSaving(true);
        // Snapshot the current selections so we can re-apply them after the
        // server clears its groups — the user's intent is "wipe results,
        // keep my assignments" until they tell us otherwise.
        const previousGroups = { ...teamGroups };
        try {
            const resp = await fetchWithRetry(
                `/api/league/${leaguePk}/cup/reset/`,
                { method: "POST" },
                0
            );
            const { ok, data } = await refreshFromResponse(resp);
            if (ok) {
                // Restore the user's prior selections in the editor; the
                // server's payload now reports empty groups, but we want the
                // editor to start from where they left off.
                const validNames = new Set(
                    Array.from(
                        {
                            length:
                                data?.cup?.config?.group_count ||
                                Number(groupCount),
                        },
                        (_, i) => GROUP_NAMES[i]
                    )
                );
                const restored = {};
                Object.entries(previousGroups).forEach(([teamId, groupName]) => {
                    if (validNames.has(groupName)) restored[teamId] = groupName;
                });
                if (Object.keys(restored).length > 0) {
                    setTeamGroups(restored);
                }
                setEditingGroups(true);
                toast.success("League Cup reset — review groups and save to rebuild it.");
            } else {
                toast.error(data.error || "Failed to reset the Cup.");
            }
        } catch {
            toast.error("An unexpected error occurred.");
        } finally {
            setSaving(false);
            setConfirmAction(null);
        }
    };

    const handleRandomize = () => {
        // Shuffle in-memory only; persistence happens on Save Groups (or
        // Create, when the cup hasn't been created yet). Calling the backend
        // randomize endpoint would overwrite any unsaved config changes the
        // user has made on the form.
        shuffleAssignments();
    };

    // Snake-draft the prior-season ranking into the current groups: ranks
    // 1..G go to Groups A..G in order, the next G in reverse, and so on.
    // Like Randomize, this is in-memory only so it respects unsaved config
    // changes (e.g. a new group count); persistence happens on Save Groups.
    const handleSeedFromPriorSeason = () => {
        const ranking = priorSeeding?.ranking || [];
        if (ranking.length === 0 || groupNames.length === 0) return;
        const next = {};
        ranking.forEach((row, index) => {
            const round = Math.floor(index / groupNames.length);
            const position = index % groupNames.length;
            const groupIndex =
                round % 2 === 0 ? position : groupNames.length - 1 - position;
            next[row.provider_team_id] = groupNames[groupIndex];
        });
        setTeamGroups(next);
    };

    // Persist the given group mapping (defaults to current `teamGroups`) to
    // the server. Returns {ok, data} so callers can chain success/error
    // handling. When `silent` is true the caller surfaces any toast.
    const persistGroups = async ({
        silent = false,
        groups = null,
        groupNamesOverride = null,
    } = {}) => {
        const source = groups || teamGroups;
        const names = groupNamesOverride || groupNames;
        const assignments = {};
        names.forEach((g) => { assignments[g] = []; });
        Object.entries(source).forEach(([teamId, groupName]) => {
            if (assignments[groupName]) assignments[groupName].push(teamId);
        });
        const resp = await fetchWithRetry(
            `/api/league/${leaguePk}/cup/groups/`,
            {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ assignments }),
            },
            0
        );
        const result = await markSavedFromResponse(resp);
        if (!silent && !result.ok) {
            (result.data.errors || [
                result.data.error || "Failed to save groups.",
            ]).forEach((e) => toast.error(e));
        }
        return result;
    };

    const handleSaveGroups = async () => {
        setSaving(true);
        try {
            // The server validates assignments against the cup's *saved*
            // group count, so unsaved config changes (e.g. 2 -> 3 groups)
            // must be applied first or the save is rejected with
            // "expected N groups".
            const configChanged = hasConfigChanges;
            if (configChanged) {
                const resp = await fetchWithRetry(
                    `/api/league/${leaguePk}/cup/`,
                    {
                        method: "PATCH",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(configBody()),
                    },
                    0
                );
                const { ok, data } = await markSavedFromResponse(resp);
                if (!ok) {
                    (data.errors || [
                        data.error || "Failed to save Cup settings.",
                    ]).forEach((e) => toast.error(e));
                    return;
                }
            }
            const { ok } = await persistGroups();
            if (ok) {
                setEditingGroups(false);
                setForceEditing(false);
                toast.success(
                    configChanged
                        ? "League Cup settings and groups saved!"
                        : "Group assignments saved!"
                );
            }
        } catch {
            toast.error("An unexpected error occurred.");
        } finally {
            setSaving(false);
        }
    };

    const handleCancelGroups = () => {
        setTeamGroups(savedAssignments);
        setEditingGroups(false);
    };

    if (loading) {
        return (
            <div className="border border-border-card rounded-xl bg-surface p-6 max-w-2xl">
                <div className="h-5 w-48 bg-border/50 rounded animate-pulse mb-3" />
                <div className="h-4 w-full bg-border/40 rounded animate-pulse" />
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="border border-border-card rounded-xl bg-surface p-6 max-w-2xl">
                <p className="text-sm text-text-muted">
                    Couldn't load League Cup settings. Refresh the page to try again.
                </p>
            </div>
        );
    }

    const locked = isConfigured && !editable;
    const formLocked = locked && !forceEditing;
    // Group editing follows the form: allowed while the cup hasn't started,
    // or once the owner explicitly enters edit mode on a started cup.
    const canEditGroups = editable || forceEditing;
    // Only assignments to a *current* group name count — a team left on
    // "Group C" after dropping to 2 groups is effectively unassigned.
    const unassignedCount =
        teamCount -
        (league?.teams || []).filter((t) =>
            groupNames.includes(teamGroups[t.provider_team_id])
        ).length;

    return (
        <div className="border border-border-card rounded-xl bg-surface p-6 max-w-2xl">
            <div className="flex items-center gap-2 mb-3">
                <Trophy className="w-5 h-5 text-mustard" />
                <span className="font-sans font-bold text-ds-text">League Cup</span>
                <span className="ml-auto flex items-center gap-1.5">
                    {locked && (
                        <span
                            className="flex items-center gap-1 text-[10px] font-sans font-bold uppercase tracking-wide px-2 py-0.5 rounded border border-border bg-bg text-text-muted"
                            title="The Cup has started — press Edit Settings to make changes."
                        >
                            <Lock className="w-3 h-3" />
                            Locked
                        </span>
                    )}
                    {cupStatus && (
                        <span className="text-xs font-sans font-bold uppercase tracking-wide px-2 py-0.5 rounded bg-forest text-white">
                            {cupStatus.replace("_", " ")}
                        </span>
                    )}
                </span>
            </div>
            <p className="text-sm text-text-muted mb-4 leading-relaxed">
                An in-season tournament layered on your real league: a points-race
                Group Stage followed by a seeded knockout bracket, scored from your
                league's actual weekly results.
            </p>

            {locked && forceEditing && (
                <div className="flex items-start gap-2 border border-mustard/40 rounded-lg px-3 py-2.5 mb-4 bg-mustard/10">
                    <Pencil className="w-4 h-4 flex-shrink-0 text-mustard mt-0.5" />
                    <span className="text-sm text-text-mid leading-relaxed">
                        Editing a started Cup — saving clears the bracket and
                        champion, then recomputes them from real results.
                    </span>
                </div>
            )}

            {/* ── Config form ── */}
            <div className="text-xs font-sans font-bold text-text-muted uppercase tracking-wide mb-2 border-t border-border pt-4">
                Tournament Format
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="col-span-2">
                    <label className={labelClass}>Cup Name</label>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className={`${inputClass} w-full`}
                    />
                </div>
                <div>
                    <label className={labelClass}>Start Week</label>
                    <input
                        type="number"
                        min={league?.min_week || 1}
                        value={startWeek}
                        onChange={(e) => setStartWeek(e.target.value)}
                        disabled={formLocked}
                        className={`${inputClass} w-full`}
                    />
                </div>
                <div>
                    <label className={labelClass}>Groups</label>
                    <select
                        value={groupCount}
                        onChange={(e) => {
                            setGroupCount(Number(e.target.value));
                            // Recompute the default Group Stage Weeks from the
                            // new group count; the previous override (if any)
                            // would silently desync from the new group sizes.
                            setGroupStageWeeks(null);
                            // A new group count invalidates (some) current
                            // assignments — surface the editor so the user
                            // can redistribute teams right away.
                            if (isConfigured && canEditGroups) {
                                setEditingGroups(true);
                            }
                        }}
                        disabled={formLocked}
                        className={`${inputClass} w-full`}
                    >
                        {[2, 3, 4].map((n) => (
                            <option key={n} value={n}>{n}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className={labelClass}>Group Stage Weeks</label>
                    <input
                        type="number"
                        min={1}
                        value={effectiveStageWeeks}
                        onChange={(e) => setGroupStageWeeks(Number(e.target.value))}
                        disabled={formLocked}
                        className={`${inputClass} w-full`}
                    />
                </div>
                <div>
                    <label className={labelClass}>Advance per Group</label>
                    <select
                        value={advancePerGroup}
                        onChange={(e) => setAdvancePerGroup(Number(e.target.value))}
                        disabled={formLocked}
                        className={`${inputClass} w-full`}
                    >
                        {advanceOptions.map((n) => (
                            <option key={n} value={n}>{n}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className={labelClass}>Weeks per Knockout Round</label>
                    <select
                        value={roundLength}
                        onChange={(e) => setRoundLength(Number(e.target.value))}
                        disabled={formLocked}
                        className={`${inputClass} w-full`}
                    >
                        {[1, 2].map((n) => (
                            <option key={n} value={n}>{n}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* ── Schedule summary ── */}
            <div className="text-sm text-text-muted border border-border rounded-lg px-3 py-2 mb-4 bg-bg">
                <span className="font-bold text-ds-text">Schedule: </span>
                Group Stage weeks {startWeek}–{Number(startWeek) + effectiveStageWeeks - 1},
                then {schedule.labels.join(" → ")} ({schedule.size}-team bracket
                {schedule.wildcards > 0 ? `, ${schedule.wildcards} wildcard${schedule.wildcards > 1 ? "s" : ""}` : ""}),
                ending week {schedule.endWeek}.
                {schedule.overruns && (
                    <span className="block mt-1 text-coral font-bold">
                        ⚠ This overruns the regular season (ends week {league?.reg_season_count}).
                    </span>
                )}
            </div>

            {/* ── Group definitions (read-only) ── */}
            {isConfigured && !editingGroups && league?.teams?.length > 0 && (
                <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-sans font-bold text-text-muted uppercase tracking-wide">
                            Group Assignments
                        </span>
                        {canEditGroups && (
                            <button
                                onClick={() => setEditingGroups(true)}
                                className="flex items-center gap-1 text-xs font-sans font-bold text-text-muted hover:text-ds-text transition-colors"
                            >
                                <Pencil className="w-3.5 h-3.5" />
                                Edit groups
                            </button>
                        )}
                    </div>
                    {unassignedCount >= teamCount ? (
                        <p className="text-sm text-text-muted">
                            No groups assigned yet.
                        </p>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {groupNames.map((g) => (
                                <div key={g} className="border border-border rounded-lg bg-bg p-3">
                                    <div className="text-[11px] font-sans font-bold uppercase tracking-wide text-text-muted mb-1.5">
                                        {g}
                                    </div>
                                    <ul className="space-y-1">
                                        {teamsByGroup[g].length === 0 ? (
                                            <li className="text-xs text-text-muted">—</li>
                                        ) : (
                                            teamsByGroup[g].map((team) => (
                                                <li
                                                    key={team.provider_team_id}
                                                    className="text-sm leading-tight"
                                                    title={team.team_name}
                                                >
                                                    <span className="font-bold text-ds-text">
                                                        {team.owner_name || team.team_name}
                                                    </span>
                                                    {team.owner_name &&
                                                        team.team_name && (
                                                            <span className="text-text-muted">
                                                                {" · "}
                                                                {team.team_name}
                                                            </span>
                                                        )}
                                                </li>
                                            ))
                                        )}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ── Group assignment editor ── */}
            {((!isConfigured) || (isConfigured && editingGroups && canEditGroups)) &&
                league?.teams?.length > 0 && (
                <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-sans font-bold text-text-muted uppercase tracking-wide">
                            {isConfigured ? "Edit Group Assignments" : "Group Assignments"}
                        </span>
                        <div className="flex items-center gap-3">
                            {priorSeeding && (
                                <Tooltip
                                    label={`Seed by Last Season: Teams are ranked by their ${
                                        priorSeeding.prior_year
                                    } regular-season Points For, then snake-drafted into groups — the top ${
                                        groupNames.length
                                    } teams go to ${groupNames.join(", ")} in order, the next ${
                                        groupNames.length
                                    } in reverse order, and so on, alternating. Teams that didn't play last season are seeded last.`}
                                >
                                    <button
                                        onClick={handleSeedFromPriorSeason}
                                        className="flex items-center gap-1 text-xs font-sans font-bold text-text-muted hover:text-ds-text transition-colors"
                                    >
                                        <ListOrdered className="w-3.5 h-3.5" />
                                        Seed by Last Season
                                    </button>
                                </Tooltip>
                            )}
                            <button
                                onClick={handleRandomize}
                                className="flex items-center gap-1 text-xs font-sans font-bold text-text-muted hover:text-ds-text transition-colors"
                            >
                                <Shuffle className="w-3.5 h-3.5" />
                                Randomize
                            </button>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {league.teams.map((team) => (
                            <div key={team.provider_team_id} className="flex items-center gap-2">
                                <div className="flex-1 min-w-0" title={team.team_name}>
                                    <div className="text-sm font-bold text-ds-text truncate">
                                        {team.owner_name || team.team_name}
                                    </div>
                                    {team.owner_name &&
                                        team.team_name && (
                                            <div className="text-xs text-text-muted truncate">
                                                {team.team_name}
                                            </div>
                                        )}
                                </div>
                                <select
                                    value={
                                        groupNames.includes(teamGroups[team.provider_team_id])
                                            ? teamGroups[team.provider_team_id]
                                            : ""
                                    }
                                    onChange={(e) =>
                                        setTeamGroups((prev) => ({
                                            ...prev,
                                            [team.provider_team_id]: e.target.value,
                                        }))
                                    }
                                    className={`${inputClass} w-28`}
                                >
                                    <option value="">—</option>
                                    {groupNames.map((g) => (
                                        <option key={g} value={g}>{g}</option>
                                    ))}
                                </select>
                            </div>
                        ))}
                    </div>
                    {unassignedCount > 0 && (
                        <p className="text-xs text-coral font-bold mt-2">
                            {unassignedCount} team{unassignedCount > 1 ? "s" : ""} unassigned.
                        </p>
                    )}
                    {isConfigured && (
                        <div className="flex gap-2 mt-2">
                            <button
                                onClick={handleSaveGroups}
                                disabled={saving || unassignedCount > 0 || !hasGroupChanges}
                                className="px-3 py-1.5 rounded-lg bg-mustard text-forest text-xs font-bold font-sans disabled:opacity-50 hover:bg-yellow-400 transition-colors"
                            >
                                Save Groups
                            </button>
                            <button
                                onClick={handleCancelGroups}
                                disabled={saving}
                                className="px-3 py-1.5 rounded-lg border border-border text-text-muted text-xs font-bold font-sans disabled:opacity-50 hover:text-ds-text hover:border-ds-text transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* ── Actions ── */}
            <div className="flex items-center gap-2 border-t border-border pt-4">
                {!isConfigured ? (
                    <button
                        onClick={handleCreate}
                        disabled={saving || schedule.overruns || unassignedCount > 0}
                        className="px-4 py-2 rounded-lg bg-mustard text-forest text-sm font-bold font-sans disabled:opacity-50 hover:bg-yellow-400 transition-colors"
                    >
                        {saving ? "Creating…" : "Create League Cup"}
                    </button>
                ) : (
                    <>
                        {locked && !forceEditing ? (
                            <button
                                onClick={() => setForceEditing(true)}
                                disabled={saving}
                                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-mustard text-forest text-sm font-bold font-sans disabled:opacity-50 hover:bg-yellow-400 transition-colors"
                            >
                                <Pencil className="w-3.5 h-3.5" />
                                Edit Settings
                            </button>
                        ) : (
                            <button
                                onClick={handleUpdate}
                                disabled={
                                    saving ||
                                    !hasConfigChanges ||
                                    schedule.overruns
                                }
                                className="px-4 py-2 rounded-lg bg-mustard text-forest text-sm font-bold font-sans disabled:opacity-50 hover:bg-yellow-400 transition-colors"
                            >
                                {saving ? "Saving…" : "Save"}
                            </button>
                        )}
                        {locked && forceEditing && (
                            <button
                                onClick={() => setForceEditing(false)}
                                disabled={saving}
                                className="px-4 py-2 rounded-lg border border-border text-text-muted text-sm font-bold font-sans disabled:opacity-50 hover:text-ds-text hover:border-ds-text transition-colors"
                            >
                                Cancel
                            </button>
                        )}
                        {!editable && (
                            <button
                                onClick={() => setConfirmAction("reset")}
                                disabled={saving}
                                className="ml-auto flex items-center gap-1.5 px-4 py-2 rounded-lg border border-coral/40 text-coral text-sm font-bold font-sans disabled:opacity-50 hover:bg-coral-light transition-colors"
                            >
                                <RotateCcw className="w-3.5 h-3.5" />
                                Reset Cup
                            </button>
                        )}
                        {editable && (
                            <button
                                onClick={() => setConfirmAction("delete")}
                                className="ml-auto px-4 py-2 rounded-lg border border-coral/40 text-coral text-sm font-bold font-sans hover:bg-coral-light transition-colors"
                            >
                                Delete
                            </button>
                        )}
                    </>
                )}
            </div>

            <ConfirmDialog
                open={confirmAction === "reset"}
                onOpenChange={(next) => !next && !saving && setConfirmAction(null)}
                onConfirm={handleReset}
                busy={saving}
                tone="warning"
                title="Reset League Cup?"
                confirmLabel="Reset Cup"
                description="Group assignments, the bracket, and the champion will be cleared — your settings are kept. The Cup recomputes from real results once groups are assigned again."
            />
            <ConfirmDialog
                open={confirmAction === "delete"}
                onOpenChange={(next) => !next && !saving && setConfirmAction(null)}
                onConfirm={handleDelete}
                busy={saving}
                tone="danger"
                title="Delete League Cup?"
                confirmLabel="Delete Cup"
                description="This permanently removes the Cup along with its configuration. This cannot be undone."
            />
        </div>
    );
};

export default LeagueCupSettings;
